'use client';

import { c as _c } from "react/compiler-runtime";
import { useIdentityContext } from '@repo/platform-analytics/IdentityContext';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useIsAnswerHydrated } from '@/entities/answer/model/hooks';
import { useFunnelSession } from '@/entities/session/model/funnel-session-context';
import { canReachStep } from '@/shared/config/funnel/branch';
import { ENTRY_STEP_ID, FINDER_ENTRY_PATH } from '@/shared/config/funnel/flowConstants';
import { useBranchId, useFunnelConfigVersion, useFunnelFlattenedConfig, useFunnelStepId, useIsBranchFirstStep, useIsFirstStep } from '../Context/hooks';
import { resolveLandingAction } from '../resolveLandingAction';
import { emptyFunnelSession } from '../session/funnel-session-reducer';
import { useFunnelContextChanges } from './context-changes';
import { isPastFirstQuestion } from './utils/is-past-first-question';

/**
 * What the funnel does on its own, called once per step page.
 *
 * `onArrive` fires once per page arrival, not when answers change, so a goal
 * change made before navigation finishes is not undone by the page it leaves.
 * `onLogin` is keyed on its own trigger and waits for landing to settle so it
 * never saves a funnel session that landing is about to discard.
 */
export function useFunnelSideEffects() {
  const $ = _c(33);
  const {
    session,
    reset,
    saveToApi,
    submit
  } = useFunnelSession();
  const isHydrated = useIsAnswerHydrated();
  const {
    isLoggedIn,
    isLoading
  } = useIdentityContext();
  const router = useRouter();
  const {
    changeGoal
  } = useFunnelContextChanges();
  const funnelFlattenedConfig = useFunnelFlattenedConfig();
  const funnelConfigVersion = useFunnelConfigVersion();
  const branchId = useBranchId();
  const stepId = useFunnelStepId();
  const isFirstStep = useIsFirstStep();
  const isBranchFirstStep = useIsBranchFirstStep();
  const t0 = session.answers[ENTRY_STEP_ID];
  let t1;
  if ($[0] !== branchId || $[1] !== funnelFlattenedConfig || $[2] !== isBranchFirstStep || $[3] !== isFirstStep || $[4] !== isHydrated || $[5] !== session.answers || $[6] !== stepId || $[7] !== t0) {
    t1 = resolveLandingAction({
      branchId,
      isHydrated,
      isFirstStep,
      isBranchFirstStep,
      branchFromAnswers: t0,
      canReachCurrentStep: branchId !== null && canReachStep(funnelFlattenedConfig, branchId, stepId, session.answers)
    });
    $[0] = branchId;
    $[1] = funnelFlattenedConfig;
    $[2] = isBranchFirstStep;
    $[3] = isFirstStep;
    $[4] = isHydrated;
    $[5] = session.answers;
    $[6] = stepId;
    $[7] = t0;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const landing = t1;
  const pageKey = `${branchId}:${stepId}`;
  let t2;
  if ($[9] !== changeGoal || $[10] !== landing.branchId || $[11] !== landing.kind || $[12] !== reset || $[13] !== router) {
    t2 = () => {
      switch (landing.kind) {
        case "none":
          {
            return;
          }
        case "reset-branch":
          {
            changeGoal(landing.branchId);
            return;
          }
        case "redirect-to-entry":
          {
            reset(emptyFunnelSession);
            router.replace(FINDER_ENTRY_PATH);
            return;
          }
      }
    };
    $[9] = changeGoal;
    $[10] = landing.branchId;
    $[11] = landing.kind;
    $[12] = reset;
    $[13] = router;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== isHydrated || $[16] !== pageKey) {
    t3 = [isHydrated, pageKey];
    $[15] = isHydrated;
    $[16] = pageKey;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  useEffect(t2, t3);
  let t4;
  if ($[18] !== isHydrated || $[19] !== isLoading || $[20] !== isLoggedIn || $[21] !== landing.kind || $[22] !== session.answers || $[23] !== session.savedAs) {
    t4 = landing.kind === "none" && isHydrated && !isLoading && isLoggedIn && isPastFirstQuestion(session.answers) && session.savedAs !== "authenticated";
    $[18] = isHydrated;
    $[19] = isLoading;
    $[20] = isLoggedIn;
    $[21] = landing.kind;
    $[22] = session.answers;
    $[23] = session.savedAs;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  const needsAuthenticatedSave = t4;
  let t5;
  if ($[25] !== funnelConfigVersion || $[26] !== needsAuthenticatedSave || $[27] !== saveToApi || $[28] !== session || $[29] !== submit) {
    t5 = () => {
      if (!needsAuthenticatedSave) {
        return;
      }
      submit(async () => {
        await saveToApi(session, funnelConfigVersion);
      });
    };
    $[25] = funnelConfigVersion;
    $[26] = needsAuthenticatedSave;
    $[27] = saveToApi;
    $[28] = session;
    $[29] = submit;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] !== needsAuthenticatedSave) {
    t6 = [needsAuthenticatedSave];
    $[31] = needsAuthenticatedSave;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  useEffect(t5, t6);
}